import React, { Component } from 'react';
import styled from 'styled-components';
import { Form, Field, change } from 'redux-form';
import { Grid, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import EntityTreeSelectContainer from 'containers/EntityTreeSelectContainer';
import StyledLabel from 'components/atoms/inputs/Label';
import SelectList from 'components/atoms/inputs/SelectList';
import Link from 'components/atoms/buttons/standard/Link';
import StyledSelect from 'components/atoms/inputs/Select';
import ButtonStandardPrimary from 'components/atoms/buttons/standard/Primary';
import ButtonStandardSecondary from 'components/atoms/buttons/standard/Secondary';
import InputTip from 'components/atoms/form/InputTip';
import FormHeading from 'components/atoms/form/FormHeading';
import FormSubHeading from 'components/atoms/form/FormSubHeading';
import TextInputAdmin from 'components/atoms/inputs/TextInputAdmin';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';
import ProtectedComponent from 'hoc/ProtectedComponent';
import { USER_ALTER_CAP_GROUPS, USER_BRAND_ASSOCIATE, USER_ORGANIZATION_BRANDING_CODE_EDIT } from 'constants/capabilities';
import CapabilityGroupSelectContainer from 'containers/CapabilityGroupSelectContainer';
import { HiddenDiv, BorderDiv } from 'components/atoms/form/UtilityDivs';
import { colors } from 'services/theme/brandTheme/brandColors';

const associatedBrandsFieldTipText = 'This will be the white-labeled brand that the user will be tied to. They will only be able to log into that brand’s subdomain.';

const UserActiveToggleWrapper = styled.div`
    margin-bottom: 15px;
    padding-left: 10px;
`;

const OrganizationBrandingCodeProtectedComponent = ProtectedComponent({
    requiredCapabilities: [USER_ORGANIZATION_BRANDING_CODE_EDIT]
})(props => (<React.Fragment>{props.children}</React.Fragment>));

const CapabilityFormField = ProtectedComponent({
    requiredCapabilities: [USER_ALTER_CAP_GROUPS]
})(() => <React.Fragment>
    <Row>
        <Col xs={6}>
            <FormSubHeading>Capability Groups</FormSubHeading>
            <Field name="CapabilityGroups" component={CapabilityGroupSelectContainer} />
        </Col>
    </Row>
</React.Fragment>);

const AssociatedBrandField = ProtectedComponent({
    requiredCapabilities: [USER_BRAND_ASSOCIATE]
})(props => <React.Fragment>
    <Row>
        <Col md={6}>
            <BorderDiv hide={props.hideAssociatedBrands}>
                <StyledLabel>Enable WhiteLabel Experience</StyledLabel>
                <ToggleSwitch onClick={props.toggleWhiteLabelClick} value={!props.hideAssociatedBrands} />
                <HiddenDiv hide={props.hideAssociatedBrands}>
                    <FormSubHeading>Associated Brand</FormSubHeading>
                    <Field
                        name="brandId"
                        list={props.sortedBrands}
                        component={SelectList} />
                    <InputTip>{associatedBrandsFieldTipText}</InputTip>
                </HiddenDiv>
            </BorderDiv>
        </Col>
    </Row>
</React.Fragment>);


class EditUserForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            brands: null,
            selectedBrandId: props.selectedBrandId,
            sortedBrands: [],
            hideAssociatedBrands: !props.selectedBrandId
        };
        this.toggleWhiteLabelClick = this.toggleWhiteLabelClick.bind(this);
        this.toggleActiveStateClick = this.toggleActiveStateClick.bind(this);
    }

    static resetBtnText(user) {
        return !user.lastLoginAt ? 'Send Invitation' : 'Reset Password';
    }

    static getDerivedStateFromProps(props, state) {
        const hasBrandsChanged = props.brands !== state.brands;
        const hasBrandIdChanged = props.selectedBrandId !== state.selectedBrandId;
        const updatedState = { ...state };

        if (hasBrandsChanged) {
            updatedState.brands = props.brands;
            updatedState.sortedBrands = props.brands.sort(EditUserForm.nameSort);
        }

        if (hasBrandIdChanged) {
            updatedState.selectedBrandId = props.selectedBrandId;
            updatedState.hideAssociatedBrands = !props.selectedBrandId;
        }

        if (!hasBrandIdChanged && state.selectedBrandId === null) {
            updatedState.hideAssociatedBrands = state.hideAssociatedBrands;
        }

        return updatedState;
    }

    static nameSort = (a, b) => `${a.name}`.localeCompare(b.name)

    isOkta() {
        return this.props.user ? this.props.user.isOkta : false;
    }

    renderUserResetButton() {
        if (this.props.user && this.isOkta() === false) {
            return (
                <ButtonStandardSecondary type="button" onClick={() => this.props.resetPassword({
                    user: {
                        ...this.props.user,
                        fullName: `${this.props.user.firstName} ${this.props.user.lastName}`
                    }
                })}>
                    {EditUserForm.resetBtnText(this.props.user)}
                </ButtonStandardSecondary>
            );
        }
        return null;
    }

    toggleWhiteLabelClick() {
        this.setState({ ...this.state, hideAssociatedBrands: !this.state.hideAssociatedBrands });
        this.props.dispatch(change('editUser', 'brandId', null));
    }

    toggleActiveStateClick() {
        this.props.dispatch(change('editUser', 'brandId', null));
    }

    renderBrandAssociation() {
        if (this.isOkta() === false) {
            return (
                <AssociatedBrandField
                    toggleWhiteLabelClick={this.toggleWhiteLabelClick}
                    hideAssociatedBrands={this.state.hideAssociatedBrands}
                    sortedBrands={this.state.sortedBrands}
                />
            );
        }
        return null;
    }

    renderHeading() {
        if (this.props.user) {
            return (
                <FormHeading>Edit User:&nbsp;
                    {this.props.user &&
                        <span>
                            {this.props.user.firstName} {this.props.user.lastName}
                        </span>
                    }
                </FormHeading>
            );
        }

        return (
            <FormHeading>Create New User</FormHeading>
        );
    }

    render() {
        const isOkta = this.isOkta();

        return (
            <Form onSubmit={this.props.handleSubmit()} id="editUserForm">
                <Grid fluid>
                    {this.renderHeading()}
                    <Row>
                        <Col md={3}>
                            <StyledLabel>First Name</StyledLabel><br />
                            <Field component={TextInputAdmin} name="firstName" type="text" />
                        </Col>
                        <Col direction="right" md={3}>
                            <StyledLabel>Last Name</StyledLabel><br />
                            <Field component={TextInputAdmin} name="lastName" type="text" />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <StyledLabel>Email</StyledLabel><br />
                            <Field component={TextInputAdmin} name="email" type="text" disabled={isOkta} />
                        </Col>
                    </Row>
                    <OrganizationBrandingCodeProtectedComponent>
                        <Row>
                            <Col md={6}>
                                <StyledLabel>Organization Branding</StyledLabel><br />
                                <Field name="meta.organizationBrandingCode" component={({ input }) => (
                                    <StyledSelect {...input}>
                                        <option value="">Goodway (Default)</option>
                                        <option value="cveLogoWhite">CvE (white)</option>
                                        <option value="cveLogoTextWhite">CvE Text (white)</option>
                                        <option value="cveLogoColor">CvE (color)</option>
                                        <option value="cveLogoTextColor">CvE Text (color)</option>
                                        <option value="cveLogoColorWhiteBG">CvE (color) White Background</option>
                                        <option value="cveLogoTextColorWhiteBG">CvE Text (color) White Background</option>
                                    </StyledSelect>
                                )} />
                            </Col>
                        </Row>
                    </OrganizationBrandingCodeProtectedComponent>

                    <Row>
                        <Col xs={6}>
                            <FormSubHeading>{`${isOkta ? 'Favorite Dashboards' : 'Entity Access'}`}</FormSubHeading>
                            <Field name="fieldEntities" key={this.props.user ? this.props.user.id : null} component={EntityTreeSelectContainer} />
                        </Col>
                    </Row>

                    <CapabilityFormField />

                    {this.state.brands.length > 0 && this.renderBrandAssociation()}

                    {!(this.props.user && this.isOkta()) ? (
                        <Row>
                            <Col md={6}>
                                <UserActiveToggleWrapper>
                                    <StyledLabel>User Active</StyledLabel>
                                    <Field component={ToggleSwitch} name='isActive' type="text" color={colors.goodwayBlue[400].hex} />
                                </UserActiveToggleWrapper>
                            </Col>
                        </Row>
                    ) : null}

                    <Row>
                        <Col xs={6}>
                            <ButtonStandardPrimary type="submit">Submit</ButtonStandardPrimary>
                            <Link onClick={this.props.handleCancel}>Cancel</Link>
                        </Col>
                        <Col xs={6}>
                            {this.renderUserResetButton()}
                        </Col>
                    </Row>
                </Grid>
            </Form>
        );
    }
}

EditUserForm.propTypes = {
    user: PropTypes.object,
    handleSubmit: PropTypes.func,
    handleCancel: PropTypes.func,
    resetPassword: PropTypes.func,
    brands: PropTypes.array.isRequired,
    selectedBrandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

export default EditUserForm;



// WEBPACK FOOTER //
// ./src/components/organisms/forms/EditUser.js